import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  FormDrawer,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerHeader,
  FormDrawerTitle,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AvatarIniciales,
  DataTable,
  Icon,
  KpiCard,
  KpiGrid,
  ProgressBar,
  StatusBadge,
  ToolbarSpacer,
  Tip,
} from "@/components/ds";
import { AlertCircle, Loader2, Plus, Save, Search, Trash } from "@/components/ds/icons";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { api, ApiError } from "@/lib/api";
import { cleanRut, formatRut } from "@/lib/rut";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";
import { TrabajadorForm } from "@/pages/personas/TrabajadorForm";
import { PapeleraTrabajadores } from "@/components/personas/PapeleraTrabajadores";
import { ConfigurarExigiblesModal } from "@/components/persona-documentos/ConfigurarExigiblesModal";
import { apiPersonaDocumentosAdapter } from "@/lib/persona-documentos-adapter";
import type { DocumentoTipo } from "@/types/documento-tipo";
import type { Trabajador } from "@/types/trabajador";
import type {
  TrabajadorResumen,
  TrabajadoresResumenResponse,
} from "@/types/trabajador-resumen";

// ─── Vocabulario y formato ───────────────────────────────────────────────────

type FiltroEstado = "todos" | "activos" | "inactivos";

/**
 * Filtro de cobertura — el select "Toda la dotación" del encabezado de la
 * tabla. Cada opción responde una pregunta operativa concreta ("¿a quién
 * tengo que perseguir hoy?"), por eso el glifo viaja bajo la clave `icon`:
 * `check-icon-subset.mjs` no ve glifos guardados con otra clave y el ícono
 * llegaría a producción dibujado como su propio nombre en letras.
 */
const COBERTURAS = [
  { valor: "todos", label: "Toda la dotación", icon: "group" },
  { valor: "doc_vencidos", label: "Con documentos vencidos", icon: "event_busy" },
  { valor: "doc_por_vencer", label: "Con documentos por vencer", icon: "schedule" },
  { valor: "doc_sin_cargar", label: "Sin documentos cargados", icon: "folder_off" },
  { valor: "cap_pendientes", label: "Con capacitaciones pendientes", icon: "school" },
  { valor: "sin_app", label: "Sin app móvil", icon: "phonelink_erase" },
] as const;

type Cobertura = (typeof COBERTURAS)[number]["valor"];

/** Bucket del filtro de área para "todavía no la clasificaron". Mismo valor
 * que usa Avance y Resultados, para que las dos pantallas se lean igual. */
const SIN_AREA = "sin_area";

function plural(n: number, singular: string, plurales: string): string {
  return `${n} ${n === 1 ? singular : plurales}`;
}

// ─── Badge de estado ────────────────────────────────────────────────────────

function EstadoBadge({ t }: { t: Trabajador }) {
  if (t.eliminado_at) {
    return (
      <Badge variant="destructive" className="gap-1">
        <Trash className="h-3 w-3" />
        En papelera
      </Badge>
    );
  }
  return (
    <Badge variant={t.activo ? "success" : "neutral"}>
      {t.activo ? "Activo" : "Inactivo"}
    </Badge>
  );
}

// ─── Celdas agregadas ───────────────────────────────────────────────────────

/**
 * Columna Documentación.
 *
 * DENOMINADOR = los tipos que la empresa declaró exigir en "Documentos que
 * exige tu empresa", NUNCA el catálogo entero. El catálogo de 48 sigue siendo
 * una lista de REFERENCIA —muchos tipos no le aplican a una empresa dada— y
 * medir contra él convertiría un checklist opcional en una nota roja
 * permanente para todo el mundo.
 *
 * Mientras la empresa no configuró nada no hay denominador legítimo: la celda
 * muestra cuántos documentos tiene el trabajador, sin fracción ni barra. Es el
 * MISMO criterio que el KPI y la tarjeta de progreso de la ficha, y las tres
 * salen de `/personas/resumen`: a la ficha se llega desde esta fila, así que no
 * pueden decir cosas distintas.
 */
function CeldaDocumentacion({
  resumen,
  exigibles,
  exigiblesConfigurados,
}: {
  resumen: TrabajadorResumen | undefined;
  exigibles: number;
  exigiblesConfigurados: boolean;
}) {
  if (!resumen) return <span className="text-soft text-[12px]">—</span>;

  const { cargados, exigibles_cargados: exigiblesCargados, vencidos, por_vencer } = resumen.documentacion;

  if (!exigiblesConfigurados || exigibles === 0) {
    return (
      <span className="text-soft text-[12px]">
        {cargados === 0 ? "Sin documentos" : `${plural(cargados, "documento", "documentos")}`}
      </span>
    );
  }

  const faltan = exigibles - exigiblesCargados;
  const nota = vencidos
    ? { texto: plural(vencidos, "vencido", "vencidos"), clase: "text-destructive" }
    : faltan > 0
      ? { texto: `${faltan} sin subir`, clase: "text-warning" }
      : por_vencer
        ? { texto: `${por_vencer} por vencer`, clase: "text-warning" }
        : { texto: "al día", clase: "text-success" };

  return (
    // `w-full` no es decorativo: la celda de `DataTable` es un flex en fila, y
    // sin ancho explícito este contenedor se encoge al tamaño de su contenido.
    // La barra, que reparte el sobrante con `flex-1`, se quedaría en unos
    // pocos píxeles y dejaría de representar ninguna proporción.
    <div className="flex w-full min-w-0 flex-col gap-1 pr-3">
      <ProgressBar
        valor={exigiblesCargados}
        total={exigibles}
        etiqueta={`${exigiblesCargados}/${exigibles}`}
      />
      <span className={`text-[10.5px] whitespace-nowrap ${nota.clase}`}>{nota.texto}</span>
    </div>
  );
}

/**
 * Columna Capacitaciones.
 *
 * El prototipo tiene un estado "1 reprobada". No existe: la decisión #1394
 * dejó cuatro resultados —completo, en curso, sin abrir, expirado— con
 * reintento ilimitado, así que nadie reprueba y no hay nota. "Pendiente" es
 * todo lo que no cerró, la MISMA definición que usa el badge del sidebar y
 * Avance y Resultados.
 */
function CeldaCapacitaciones({ resumen }: { resumen: TrabajadorResumen | undefined }) {
  if (!resumen) return <span className="text-soft text-[12px]">—</span>;

  const { total, pendientes } = resumen.capacitaciones;

  if (total === 0) return <StatusBadge estado="no_asignado">Sin envíos</StatusBadge>;
  if (pendientes > 0) {
    return (
      <StatusBadge estado="atrasado">{plural(pendientes, "pendiente", "pendientes")}</StatusBadge>
    );
  }
  return <StatusBadge estado="completo">Al día</StatusBadge>;
}

// ─── Componente principal ────────────────────────────────────────────────────

export function Trabajadores() {
  const [, navigate] = useLocation();
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [resumenes, setResumenes] = useState<Map<string, TrabajadorResumen>>(new Map());
  // El `meta` describe a la EMPRESA (cuántos tipos exige), no a cada
  // trabajador: es el denominador compartido por todas las filas.
  const [metaResumen, setMetaResumen] = useState<TrabajadoresResumenResponse["meta"] | null>(null);

  // Exigibles — es una decisión de EMPRESA sobre toda la dotación, así que su
  // lugar natural es acá y no dentro de la ficha de una persona. La ficha
  // conserva su enlace porque es donde uno NOTA que falta configurarlo, pero
  // el punto de entrada de la política vive en el listado.
  const [tiposCatalogo, setTiposCatalogo] = useState<DocumentoTipo[]>([]);
  const [exigiblesAbierto, setExigiblesAbierto] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>("todos");
  const [cobertura, setCobertura] = useState<Cobertura>("todos");
  const [filtroArea, setFiltroArea] = useState("");
  const [filtroCargo, setFiltroCargo] = useState("");
  const [verPapelera, setVerPapelera] = useState(false);
  const [papelera, setPapelera] = useState<Trabajador[]>([]);
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);

  // --- Guía de Trabajadores ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Desactiva el focus trap de Radix para que los botones Siguiente/Anterior
   * de driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog.
   *
   * Paso 2 — Highlight del botón Nuevo Trabajador:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default.
   *   - onNextClick: abre el sheet lateral, activa modal={false} y hace poll rAF
   *     para esperar que monte [data-tour="trabajador-rut"] antes de avanzar.
   *
   * Pasos 3–5 — Campos dentro del diálogo:
   *   - driver en z default (~10000), por encima del Radix Sheet (z-50).
   *   - modal={false} → focus trap desactivado → botones driver clickeables.
   *
   * Paso 6 — Botón Guardar: sale de guideInDialog para restaurar modal normal.
   */
  const buildTrabajadoresGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura
    {
      popover: {
        title: "¿Por qué registrar trabajadores?",
        description:
          "Los trabajadores son el núcleo del sistema de prevención de riesgos. Sin trabajadores con un cargo asignado no es posible emitir entregas de EPP, registros de capacitación ni checklists de vehículos. Registremos el primero.",
      },
    },
    // Paso 2 — Highlight del botón Nuevo Trabajador (pre-diálogo)
    {
      element: '[data-tour="trabajadores-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Registrar un trabajador",
        description:
          "Desde aquí agregas a las personas que forman parte del equipo de tu empresa. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            if (document.querySelector('[data-tour="trabajador-rut"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Campo RUT (primer campo visible, identidad del trabajador)
    {
      element: '[data-tour="trabajador-rut"]',
      popover: {
        title: "RUT del trabajador",
        description:
          "El RUT es el identificador legal único de cada persona en Chile. Es obligatorio y debe ser válido — el sistema lo valida con dígito verificador.",
      },
    },
    // Paso 4 — Campo Cargo (rol en la empresa)
    {
      element: '[data-tour="trabajador-cargo"]',
      popover: {
        title: "Cargo del trabajador",
        description:
          "El cargo define el rol del trabajador en la empresa. Es obligatorio: sin cargo no se puede emitir documentación de seguridad asociada a su función.",
      },
    },
    // Paso 5 — Campo Áreas (clasificación operativa)
    {
      element: '[data-tour="trabajador-areas"]',
      popover: {
        title: "Áreas del trabajador",
        description:
          "Las áreas clasifican al trabajador para filtros y reportes. Son opcionales: si tu empresa no trabaja con áreas, puedes dejarlo sin asignar.",
      },
    },
    // Paso 6 — Botón Guardar
    {
      element: '[data-tour="trabajadores-guardar"]',
      popover: {
        title: "Guardar trabajador",
        description:
          "Cuando hayas completado los datos obligatorios, guarda para registrar al trabajador.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 7 — Popover de cierre
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras usando el botón de ayuda en esta página o desde el panel de primeros pasos.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-trabajadores", { buildSteps: buildTrabajadoresGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-trabajadores", startGuide, { anchorSelector: '[data-tour="trabajadores-crear"]' });
  useGuideAutostart("guide-trabajadores", startGuide, { anchorSelector: '[data-tour="trabajadores-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía de Trabajadores ---

  // Restaurar desde papelera
  const [aRestaurar, setARestaurar] = useState<Trabajador | null>(null);
  const [restaurando, setRestaurando] = useState(false);

  // Purgar (hard delete, confirmación fuerte)
  const [aPurgar, setAPurgar] = useState<Trabajador | null>(null);
  const [purgando, setPurgando] = useState(false);
  const [confirmPurgarTexto, setConfirmPurgarTexto] = useState("");

  // Activar/inactivar en lote. El toggle de UNO vive en `TrabajadorForm`
  // (botón Editar de la fila y pestaña Información de la ficha), que ya trae
  // su propia confirmación explicando que inactivar retira la licencia de app.
  // Duplicar acá ese diálogo sería un segundo lugar donde mantener el mismo
  // texto legal.
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  /**
   * El listado y el resumen se piden en paralelo y se tratan distinto: sin el
   * listado no hay pantalla, mientras que sin el resumen la pantalla sigue
   * siendo útil (identidad, estado, papelera) y sólo se degradan tres
   * columnas a "—". Por eso el fallo del resumen NO levanta `loadError`.
   */
  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [vivos, enPapelera] = await Promise.all([
        api.get<{ data: Trabajador[] }>("/personas?all=1"),
        api.get<{ data: Trabajador[] }>("/personas?all=1&en_papelera=1"),
      ]);
      setTrabajadores(vivos.data);
      setPapelera(enPapelera.data);
    } catch {
      setLoadError("No se pudo cargar el listado de trabajadores.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // El catálogo se pide una vez y sólo alimenta al modal: el listado no lo
  // necesita para pintar sus columnas. Silencioso si falla — el botón queda
  // sin datos y no se puede abrir, pero la pantalla sigue siendo útil.
  const cargarCatalogo = useCallback(async () => {
    try {
      setTiposCatalogo((await apiPersonaDocumentosAdapter.listTipos()).tipos);
    } catch {
      setTiposCatalogo([]);
    }
  }, []);

  const cargarResumen = useCallback(async () => {
    try {
      const res = await api.get<TrabajadoresResumenResponse>("/personas/resumen");
      setResumenes(new Map(res.data.map((r) => [r.persona_uuid, r])));
      setMetaResumen(res.meta);
    } catch {
      // Silencioso a propósito: un toast de error por una columna secundaria
      // en cada carga de pantalla es ruido. Las celdas ya dicen "—".
      setResumenes(new Map());
      setMetaResumen(null);
    }
  }, []);

  const guardarExigidos = useCallback(
    async (tipoIds: number[]) => {
      const catalogo = await apiPersonaDocumentosAdapter.guardarExigidos(tipoIds);
      setTiposCatalogo(catalogo.tipos);
      toast.success("Listo, guardamos qué documentos exige tu empresa.");
      // El denominador de la columna Documentación acaba de cambiar para TODAS
      // las filas, no sólo para una: sin esto la tabla seguiría mostrando el
      // anterior hasta recargar la página.
      await cargarResumen();
    },
    [cargarResumen],
  );

  useEffect(() => {
    cargarCatalogo();
  }, [cargarCatalogo]);

  useEffect(() => {
    cargar();
    cargarResumen();
  }, [cargar, cargarResumen]);

  /** Tras cualquier mutación: el listado y los agregados vuelven juntos. */
  const recargar = useCallback(() => {
    cargar();
    cargarResumen();
  }, [cargar, cargarResumen]);

  // ── Restaurar desde papelera ──────────────────────────────────────────────
  const restaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      await api.post(`/personas/${aRestaurar.id}/restaurar`, {});
      toast.success(
        `Trabajador "${aRestaurar.nombre} ${aRestaurar.apellido}" restaurado. Ya puede volver a ingresar.`,
      );
      setARestaurar(null);
      recargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este trabajador no está en la papelera.");
      } else {
        toast.error("No se pudo restaurar el trabajador.");
      }
    } finally {
      setRestaurando(false);
    }
  };

  // ── Purgar (hard delete, irreversible) ────────────────────────────────────
  const purgar = async () => {
    if (!aPurgar) return;
    setPurgando(true);
    try {
      await api.delete(`/personas/${aPurgar.id}/purgar`);
      toast.success(
        `Trabajador "${aPurgar.nombre} ${aPurgar.apellido}" eliminado permanentemente.`,
      );
      setAPurgar(null);
      setConfirmPurgarTexto("");
      recargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const msg = err.message ?? "";
        if (msg.includes("tiene_evidencia_firmada") || msg.includes("evidencia")) {
          toast.error(
            "No se puede purgar este trabajador porque tiene documentos firmados (evidencia legal). Para preservar la trazabilidad, el purgado permanente está bloqueado.",
            { duration: 8000 },
          );
        } else {
          toast.error("Solo se puede purgar desde la papelera.");
        }
      } else {
        toast.error("No se pudo purgar el trabajador.");
      }
      setAPurgar(null);
      setConfirmPurgarTexto("");
    } finally {
      setPurgando(false);
    }
  };

  /**
   * Activación/inactivación masiva de los seleccionados.
   *
   * Secuencial, no `Promise.all`: son N escrituras sobre la misma empresa y
   * cada inactivación libera un cupo de licencia. En paralelo, dos que corren
   * a la vez pueden leer el mismo saldo de cupos. Con una dotación real la
   * diferencia de tiempo es irrelevante; la de correctitud, no.
   */
  const cambiarEstadoSeleccion = async (activo: boolean) => {
    const objetivos = trabajadores.filter((t) => seleccion.has(t.id) && t.activo !== activo);
    if (objetivos.length === 0) {
      toast.info(
        activo
          ? "Los trabajadores seleccionados ya están activos."
          : "Los trabajadores seleccionados ya están inactivos.",
      );
      return;
    }

    setCambiandoEstado(true);
    let ok = 0;
    for (const t of objetivos) {
      try {
        await api.patch(`/personas/${t.id}/estado`, { activo });
        ok += 1;
      } catch {
        // Se sigue con el resto: un fallo aislado no debe abortar el lote.
      }
    }
    setCambiandoEstado(false);
    setSeleccion(new Set());

    if (ok === objetivos.length) {
      toast.success(
        activo
          ? `${plural(ok, "trabajador quedó activo", "trabajadores quedaron activos")}.`
          : `${plural(ok, "trabajador quedó inactivo", "trabajadores quedaron inactivos")}: salen de las asignaciones y liberan su licencia de app.`,
      );
    } else {
      toast.error(
        `Se actualizaron ${ok} de ${objetivos.length}. Revisa el listado y reintenta con los que quedaron.`,
      );
    }
    recargar();
  };

  // ── Opciones de filtro derivadas de la dotación cargada ───────────────────
  // Salen del propio listado en vez de dos endpoints extra: los cargos y áreas
  // que importan para filtrar son los que alguien tiene asignados.
  const areas = useMemo(() => {
    const porId = new Map<number, { id: number; nombre: string; slug?: string | null }>();
    for (const t of trabajadores) for (const a of t.areas) porId.set(a.id, a);
    return ordenarAreas([...porId.values()]);
  }, [trabajadores]);

  const cargos = useMemo(() => {
    const porId = new Map<number, { id: number; nombre: string }>();
    for (const t of trabajadores) if (t.cargo) porId.set(t.cargo.id, t.cargo);
    return [...porId.values()].sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" }),
    );
  }, [trabajadores]);

  // ── Filtrado ──────────────────────────────────────────────────────────────
  const coincideCobertura = useCallback(
    (t: Trabajador): boolean => {
      if (cobertura === "todos") return true;
      if (cobertura === "sin_app") return !t.tiene_licencia;

      const r = resumenes.get(t.uuid);
      // Sin resumen no se puede afirmar nada del trabajador: excluirlo es
      // preferible a incluirlo y hacer creer que cumple el criterio.
      if (!r) return false;

      switch (cobertura) {
        case "doc_vencidos":
          return r.documentacion.vencidos > 0;
        case "doc_por_vencer":
          return r.documentacion.por_vencer > 0;
        case "doc_sin_cargar":
          return r.documentacion.cargados === 0;
        case "cap_pendientes":
          return r.capacitaciones.pendientes > 0;
      }
    },
    [cobertura, resumenes],
  );

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rutQuery = q ? cleanRut(q) : "";
    return trabajadores.filter((t) => {
      if (filtroEstado === "activos" && !t.activo) return false;
      if (filtroEstado === "inactivos" && t.activo) return false;

      if (filtroArea === SIN_AREA) {
        if (t.areas.length > 0) return false;
      } else if (filtroArea && !t.areas.some((a) => String(a.id) === filtroArea)) {
        return false;
      }

      if (filtroCargo && String(t.cargo?.id ?? "") !== filtroCargo) return false;
      if (!coincideCobertura(t)) return false;

      if (!q) return true;
      const fullName = `${t.nombre} ${t.apellido}`.toLowerCase();
      return (
        fullName.includes(q) ||
        (rutQuery !== "" && t.rut.toLowerCase().includes(rutQuery)) ||
        (t.cargo?.nombre ?? "").toLowerCase().includes(q)
      );
    });
  }, [trabajadores, search, filtroEstado, filtroArea, filtroCargo, coincideCobertura]);

  const conteos = useMemo(
    () => ({
      todos: trabajadores.length,
      activos: trabajadores.filter((t) => t.activo).length,
      inactivos: trabajadores.filter((t) => !t.activo).length,
    }),
    [trabajadores],
  );

  /**
   * Los KPI se pliegan de las MISMAS filas que la tabla y sobre la dotación
   * COMPLETA, no sobre el subconjunto filtrado: viven encima de los filtros y
   * describen la empresa, no la búsqueda en curso. Cada `detalle` dice de qué
   * universo salió el número — un KPI sin denominador explícito no se puede
   * reconciliar con ninguna otra pantalla.
   */
  const kpis = useMemo(() => {
    const total = trabajadores.length;
    const conResumen = trabajadores
      .map((t) => resumenes.get(t.uuid))
      .filter((r): r is TrabajadorResumen => r !== undefined);

    // "Al día" = no le falta ningún exigible y no tiene nada vencido ni por
    // vencer. Sin exigibles configurados no hay nada que faltar, así que la
    // condición cae a la que había antes: tener documentos y estar vigentes.
    const exigibles = metaResumen?.exigibles ?? 0;
    const midePorExigibles = Boolean(metaResumen?.exigibles_configurados) && exigibles > 0;
    const alDia = conResumen.filter((r) => {
      const { cargados, exigibles_cargados: exigiblesCargados, vencidos, por_vencer } = r.documentacion;
      if (vencidos > 0 || por_vencer > 0) return false;
      return midePorExigibles ? exigiblesCargados >= exigibles : cargados > 0;
    }).length;
    const conVencidos = conResumen.filter((r) => r.documentacion.vencidos > 0).length;
    const conPendientes = conResumen.filter((r) => r.capacitaciones.pendientes > 0).length;
    const conEnvios = conResumen.filter((r) => r.capacitaciones.total > 0).length;
    const conApp = trabajadores.filter((t) => t.tiene_licencia).length;

    return { total, alDia, conVencidos, conPendientes, conEnvios, conApp };
  }, [trabajadores, resumenes, metaResumen]);

  // ── Selección ─────────────────────────────────────────────────────────────
  // La selección se limpia cuando cambia el conjunto visible: mantener marcada
  // una fila que el filtro ya sacó de pantalla convierte "Desactivar" en una
  // acción sobre gente que el usuario no está viendo.
  useEffect(() => {
    setSeleccion(new Set());
  }, [filtroEstado, filtroArea, filtroCargo, cobertura, search]);

  const alternarSeleccion = (id: number) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const seleccionados = filtrados.filter((t) => seleccion.has(t.id));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trabajadores"
        subtitle="Dotación de la empresa activa, con el estado de documentos y capacitaciones."
        rightContent={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setExigiblesAbierto(true)}
              disabled={tiposCatalogo.length === 0}
              className="shrink-0"
            >
              <Icon name="fact_check" size={16} />
              Documentos exigidos
            </Button>
            <Button data-tour="trabajadores-crear" onClick={() => setCreateOpen(true)} className="shrink-0">
              <Plus className="h-4 w-4" />
              Nuevo trabajador
            </Button>
          </div>
        }
      />

      {/*
        modal={false} desactiva el focus trap de Radix Sheet durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del sheet) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el sheet es modal.
      */}
      <FormDrawer
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setGuideInDialog(false);
          setCreateOpen(open);
        }}
        side="right"
        modal={guideInDialog ? false : undefined}
        dismissible={guideInDialog ? false : undefined}
      >
        <TrabajadorFormDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            recargar();
            invalidate();
          }}
        />
      </FormDrawer>

      {/*
        Va al NIVEL DE LA PÁGINA, no dentro de ninguna de las ramas del listado.
        Se abre desde el botón del encabezado, que está visible en Dotación y en
        Papelera: montarlo dentro de una rama lo deja desmontado en la otra y el
        botón cambia el estado sin que aparezca nada en pantalla.
      */}
      <ConfigurarExigiblesModal
        open={exigiblesAbierto}
        onOpenChange={setExigiblesAbierto}
        tipos={tiposCatalogo}
        dotacion={trabajadores.length}
        origen="listado"
        onGuardar={guardarExigidos}
      />

      <KpiGrid min={196}>
        <KpiCard
          label="Con documentación al día"
          valor={`${kpis.alDia}/${kpis.total}`}
          detalle="con documentos cargados y ninguno vencido ni por vencer"
        />
        <KpiCard
          label="Con documentos vencidos"
          valor={kpis.conVencidos}
          alerta={kpis.conVencidos > 0}
          detalle={`de ${plural(kpis.total, "trabajador", "trabajadores")} en la dotación`}
        />
        <KpiCard
          label="Con capacitaciones pendientes"
          valor={kpis.conPendientes}
          detalle={`de ${kpis.conEnvios} con capacitaciones ya enviadas`}
        />
        <KpiCard
          label="Con app móvil"
          valor={kpis.conApp}
          detalle={`de ${plural(kpis.total, "trabajador", "trabajadores")} en la dotación`}
        />
      </KpiGrid>

      <ToggleGroup
        type="single"
        variant="segmented"
        value={verPapelera ? "papelera" : "dotacion"}
        onValueChange={(v) => {
          if (!v) return;
          setVerPapelera(v === "papelera");
          setSearch("");
          setSeleccion(new Set());
        }}
      >
        <ToggleGroupItem value="dotacion">
          Dotación
          <ToggleGroupCount>{trabajadores.length}</ToggleGroupCount>
        </ToggleGroupItem>
        <ToggleGroupItem value="papelera">
          <Icon name="delete" size={16} />
          Papelera
          <ToggleGroupCount>{papelera.length}</ToggleGroupCount>
        </ToggleGroupItem>
      </ToggleGroup>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-muted-foreground">{loadError}</p>
            <button type="button" onClick={recargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : verPapelera ? (
        <Card>
          <CardContent className="pt-6">
            <PapeleraTrabajadores
              trabajadores={papelera}
              onRestaurar={setARestaurar}
              onEliminar={(t) => {
                setAPurgar(t);
                setConfirmPurgarTexto("");
              }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-3 pt-6">
            {seleccionados.length > 0 && (
              <div className="bg-primary-50 border-primary-border flex flex-wrap items-center gap-2.5 rounded-xl border px-3.5 py-2.5">
                <span className="text-primary-hover flex-1 text-[12.5px] font-medium">
                  {plural(
                    seleccionados.length,
                    "trabajador seleccionado",
                    "trabajadores seleccionados",
                  )}
                </span>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={cambiandoEstado}
                  onClick={() => cambiarEstadoSeleccion(true)}
                >
                  <Icon name="how_to_reg" size={15} />
                  Activar
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={cambiandoEstado}
                  onClick={() => cambiarEstadoSeleccion(false)}
                >
                  <Icon name="person_off" size={15} />
                  Desactivar
                </Button>
                <Button variant="ghost" size="xs" onClick={() => setSeleccion(new Set())}>
                  Quitar selección
                </Button>
              </div>
            )}

            <DataTable
              rows={filtrados}
              getRowKey={(t) => t.id}
              minWidth={856}
              pageSize={20}
              onRowClick={(t) => navigate(`/personas/trabajadores/${t.uuid}/ficha`)}
              emptyMessage={
                trabajadores.length === 0
                  ? "Todavía no hay trabajadores registrados."
                  : "Ningún trabajador coincide con estos filtros."
              }
              emptyIcon={trabajadores.length === 0 ? "group_add" : "person_search"}
              toolbar={
                <>
                  <ToggleGroup
                    type="single"
                    variant="segmented"
                    value={filtroEstado}
                    onValueChange={(v) => v && setFiltroEstado(v as FiltroEstado)}
                  >
                    <ToggleGroupItem value="todos">
                      Todos
                      <ToggleGroupCount>{conteos.todos}</ToggleGroupCount>
                    </ToggleGroupItem>
                    <ToggleGroupItem value="activos">
                      Activos
                      <ToggleGroupCount>{conteos.activos}</ToggleGroupCount>
                    </ToggleGroupItem>
                    <ToggleGroupItem value="inactivos">
                      Inactivos
                      <ToggleGroupCount>{conteos.inactivos}</ToggleGroupCount>
                    </ToggleGroupItem>
                  </ToggleGroup>

                  <div className="relative min-w-[190px] flex-1">
                    <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                    <Input
                      placeholder="Buscar por nombre, RUT o cargo…"
                      className="h-9 pl-8 text-[13px]"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  <NativeSelect
                    aria-label="Filtrar por cobertura"
                    value={cobertura}
                    onChange={(e) => setCobertura(e.target.value as Cobertura)}
                  >
                    {COBERTURAS.map((c) => (
                      <NativeSelectOption key={c.valor} value={c.valor}>
                        {c.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>

                  {/* Las áreas son un clasificador OPCIONAL: el select siempre
                      trae la opción vacía y nunca restringe el listado por
                      defecto. */}
                  <NativeSelect
                    aria-label="Filtrar por área"
                    value={filtroArea}
                    onChange={(e) => setFiltroArea(e.target.value)}
                  >
                    <NativeSelectOption value="">Todas las áreas</NativeSelectOption>
                    <NativeSelectOption value={SIN_AREA}>Sin área</NativeSelectOption>
                    {areas.map((a) => (
                      <NativeSelectOption key={a.id} value={String(a.id)}>
                        {formatNombreArea(a)}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>

                  <NativeSelect
                    aria-label="Filtrar por cargo"
                    value={filtroCargo}
                    onChange={(e) => setFiltroCargo(e.target.value)}
                  >
                    <NativeSelectOption value="">Todos los cargos</NativeSelectOption>
                    {cargos.map((c) => (
                      <NativeSelectOption key={c.id} value={String(c.id)}>
                        {c.nombre}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>

                  <ToolbarSpacer />
                </>
              }
              columns={[
                {
                  key: "seleccion",
                  header: "Seleccionar",
                  width: "28px",
                  srOnlyHeader: true,
                  cell: (t) => (
                    // stopPropagation: la fila entera navega a la ficha, y
                    // marcar el checkbox no debe salir de la pantalla.
                    <span onClick={(e) => e.stopPropagation()} className="flex items-center">
                      <Checkbox
                        checked={seleccion.has(t.id)}
                        onCheckedChange={() => alternarSeleccion(t.id)}
                        aria-label={`Seleccionar a ${t.nombre} ${t.apellido}`}
                      />
                    </span>
                  ),
                },
                {
                  key: "trabajador",
                  header: "Trabajador",
                  width: "minmax(180px,1.7fr)",
                  cell: (t) => (
                    <div className="flex min-w-0 items-center gap-2.5 pr-3">
                      {/* El tono repite lo que ya dice el badge de estado de la
                          fila. No es redundancia: barriendo la columna con la
                          vista, el gris de un inactivo salta antes que su
                          badge, que está tres columnas más allá. */}
                      <AvatarIniciales
                        iniciales={t.iniciales}
                        tamano="listado"
                        tono={t.activo ? "teal" : "neutro"}
                      />
                      <div className="flex min-w-0 flex-col gap-px">
                        <span className="fc-cell fc-truncate">
                          {t.nombre} {t.apellido}
                        </span>
                        <span className="fc-num text-soft text-[10.5px]">{formatRut(t.rut)}</span>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "cargo",
                  header: "Cargo y área",
                  width: "minmax(130px,1.1fr)",
                  cell: (t) => (
                    <div className="flex min-w-0 flex-col gap-px pr-3">
                      <span className="fc-truncate text-[12.5px]">
                        {t.cargo?.nombre ?? <span className="text-soft italic">Sin cargo</span>}
                      </span>
                      <span className="fc-truncate text-soft text-[11px]">
                        {t.areas.length > 0
                          ? ordenarAreas(t.areas).map(formatNombreArea).join(" · ")
                          : "Sin área"}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "documentacion",
                  header: "Documentación",
                  // 158px es el mínimo con el que la barra sigue siendo una
                  // barra: por debajo, `ProgressBar` reparte el ancho con su
                  // etiqueta y queda un guion de unos pocos píxeles que no
                  // comunica ninguna proporción.
                  width: "158px",
                  cell: (t) => (
                    <CeldaDocumentacion
                      resumen={resumenes.get(t.uuid)}
                      exigibles={metaResumen?.exigibles ?? 0}
                      exigiblesConfigurados={metaResumen?.exigibles_configurados ?? false}
                    />
                  ),
                },
                {
                  key: "capacitaciones",
                  header: "Capacitaciones",
                  width: "126px",
                  cell: (t) => <CeldaCapacitaciones resumen={resumenes.get(t.uuid)} />,
                },
                {
                  key: "estado",
                  header: "Estado",
                  width: "104px",
                  cell: (t) => {
                    // El glifo va dentro de un span con `title` y texto
                    // sr-only: `Icon` se renderiza con aria-hidden, así que un
                    // `title` puesto sobre él no lo lee ningún lector.
                    const appLabel = t.tiene_licencia
                      ? "Con licencia de la app móvil"
                      : "Sin app móvil";
                    return (
                      <div className="flex items-center gap-1.5">
                        <EstadoBadge t={t} />
                        <Tip texto={appLabel}>
                          <span className="flex items-center">
                            <Icon
                              name={t.tiene_licencia ? "phone_iphone" : "phonelink_erase"}
                              size={15}
                              className={t.tiene_licencia ? "text-primary-hover" : "text-icon-soft"}
                            />
                            <span className="sr-only">{appLabel}</span>
                          </span>
                        </Tip>
                      </div>
                    );
                  },
                },
                {
                  // Editar y Mover a la papelera vivían acá y se fueron a la
                  // Ficha, que es donde el diseño las pone: la fila abre la
                  // ficha completa, así que un botón de editar al lado era un
                  // segundo camino a lo mismo, y borrar desde una fila —sin ver
                  // la documentación ni las firmas de esa persona— es
                  // justamente la decisión que conviene tomar con contexto
                  // delante. Queda el chevron, que dice a dónde lleva la fila.
                  key: "abrir",
                  header: "Abrir ficha",
                  width: "32px",
                  align: "right",
                  srOnlyHeader: true,
                  cell: () => (
                    <Icon name="chevron_right" size={18} className="text-icon-soft" />
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>
      )}

      {/* Confirmar: restaurar */}
      <AlertDialog
        open={aRestaurar !== null}
        onOpenChange={(open) => !open && setARestaurar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar trabajador</AlertDialogTitle>
            <AlertDialogDescription>
              {aRestaurar ? (
                <>
                  ¿Restaurar a{" "}
                  <strong>
                    {aRestaurar.nombre} {aRestaurar.apellido}
                  </strong>
                  ? El trabajador vuelve al listado activo y puede volver a ingresar al sistema.
                  Su estado de activación (activo/inactivo) se mantiene sin cambios.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                restaurar();
              }}
              disabled={restaurando}
            >
              {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: purgar (typed-confirm) */}
      <AlertDialog
        open={aPurgar !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAPurgar(null);
            setConfirmPurgarTexto("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Eliminar permanentemente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aPurgar ? (
                  <div className="space-y-3">
                    <p>
                      Estás por eliminar definitivamente a{" "}
                      <strong>
                        {aPurgar.nombre} {aPurgar.apellido}
                      </strong>
                      . Esta acción es <strong>irreversible</strong>: se borrarán todos sus datos,
                      licencias y accesos. Los documentos firmados (evidencia legal) también se
                      perderán si los hubiera.
                    </p>
                    <p className="font-medium">
                      Escribe <span className="font-mono text-destructive">ELIMINAR</span> para
                      confirmar:
                    </p>
                    <Input
                      value={confirmPurgarTexto}
                      onChange={(e) => setConfirmPurgarTexto(e.target.value)}
                      placeholder="ELIMINAR"
                      className="font-mono"
                      disabled={purgando}
                      autoFocus
                    />
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgando}
              onClick={() => {
                setAPurgar(null);
                setConfirmPurgarTexto("");
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                purgar();
              }}
              disabled={purgando || confirmPurgarTexto !== "ELIMINAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Eliminar permanentemente"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface TrabajadorFormDrawerProps {
  mode: "create" | "edit";
  trabajador?: Trabajador;
  onSaved: () => void;
}

/**
 * Envoltorio del diálogo de edición rápida — el cuerpo de campos vive en
 * `TrabajadorForm` (compartido también con Tab1 · Info de la Ficha del
 * trabajador). El comportamiento de edición rápida queda intacto.
 */
function TrabajadorFormDrawer({ mode, trabajador, onSaved }: TrabajadorFormDrawerProps) {
  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <FormDrawerTitle>
          {mode === "create" ? "Nuevo trabajador" : "Editar trabajador"}
        </FormDrawerTitle>
        <FormDrawerDescription>
          {mode === "create"
            ? "Registra una persona del staff de tu empresa."
            : "Actualiza los datos del trabajador."}
        </FormDrawerDescription>
      </FormDrawerHeader>

      <TrabajadorForm
        mode={mode}
        trabajador={trabajador}
        onSaved={onSaved}
        formClassName={formDrawerBodyClass}
        renderFooter={({ isSubmitting }) => (
          <FormDrawerFooter>
            <Button type="submit" disabled={isSubmitting} className="gap-2" data-tour="trabajadores-guardar">
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {mode === "create" ? "Crear trabajador" : "Guardar cambios"}
            </Button>
          </FormDrawerFooter>
        )}
      />
    </FormDrawerContent>
  );
}
